// Figuren statt Kreise (Phase Kern, T21, T30, T31, K38, K39): die Bilder des Grafik-Leads auf dem Ufer. Ein farbiger
// Ring zeigt die Klasse, Befallene stehen auf einem gestrichelten violetten Ring, Sturmwesen auf ihrem Schatten.
// Alles, was am Kreis hängt (Lebensbalken, Name, Mut, Kerben, Wörter), zeichnet render.ts weiter, nur an der Figur
// ausgerichtet. Fehlt ein Bild, bleibt der Kreis (kern.md).
import { getTrait } from '../content/index.ts';
import { FrameFlag, type BattleEvent, type BattleResult } from '../sim/index.ts';
import { animFrame, blit, drawStrip, figuresWanted, fxFrame, image, lookImage, pixelScale, pixelsPerUnit, point1, pointList, silhouetteOf, sprite, stripFrame, stripSeconds, whiteOf, type ArtSheet, type ArtSprite, type GlowSpec } from './art.ts';
import { KNIFF_HITS, motionOf } from './shore/motion.ts';
import type { DrawUnit } from './render.ts';
import type { ActiveFx } from './shore/signs.ts';

/** Wo eine Figur steht und wie groß sie gezeichnet wird, alles in Gerätepixeln */
export type FigureGeo = {
  /** Fußpunkt */
  x: number;
  y: number;
  /** Oberkante des Kopfes und Mitte des Körpers */
  top: number;
  cy: number;
  /** Breite der Figur */
  w: number;
  /** Gerätepixel je Bildpixel */
  k: number;
  ringRx: number;
  ringRy: number;
  sheet: ArtSheet | null;
  /** Ohne Bewegungsbilder: das einzelne Bild */
  still: ArtSprite | null;
  flip: boolean;
};

export const INFESTED_RING = '#b07ce8';
const TRAIT_COLOR = '#c99cff';

function spriteOf(u: DrawUnit): ArtSprite | undefined {
  if (u.kind === 'tree') return sprite('marker', 'baum');
  if (!u.art) return undefined;
  return u.kind === 'stormling' ? sprite('enemy', u.art) : sprite('species', u.art);
}

/** Die Figur zu einer Einheit, oder null: dann zeichnet render.ts den Kreis */
export function figureGeo(u: DrawUnit, s: number): FigureGeo | null {
  if (!figuresWanted()) return null;
  const sp = spriteOf(u);
  if (!sp) return null;
  const sheet = (u.kind === 'infested' ? sp.infested : sp.sheet) ?? null;
  const sheetReady = !!sheet && !!image(sheet.file);
  const still = image(sp.file) && sp.anchor && sp.size && u.kind !== 'infested' ? sp : null;
  if (!sheetReady && !still) return null;
  const k = pixelScale(s);
  const lift = (u.pose?.lift ?? 0) * s;
  let x = u.x * s;
  let y = u.y * s - lift;
  // S5: Das Eichhörnchen sitzt im Baum, am Punkt sitz; beim Absprung und bei der Rückkehr zählt die Höhe mit
  const inTree = (u.flags & FrameFlag.onTree) !== 0 && u.kind !== 'tree' ? 1 : (u.pose?.treeLift ?? 0);
  if (inTree > 0) {
    const tree = sprite('marker', 'baum');
    const sitz = point1(tree?.points, 'sitz');
    if (tree?.anchor && sitz) y -= (tree.anchor[1] - sitz[1]) * k * inTree;
  }
  y += (u.pose?.drift ?? 0) * s;
  // K44: Ausfallschritt beim Schlag; nach dem Abschied geht der Freund davon
  x += ((u.pose?.lunge?.x ?? 0) + (u.pose?.walkX ?? 0)) * s;
  y += (u.pose?.lunge?.y ?? 0) * s;
  // K38 Feige auf der Flucht: die Figur wackelt um einen Bildpixel
  x += (u.wobble ?? 0) * k;
  const anchorY = sheetReady ? sheet!.anchor[1] : sp.anchor![1];
  const kopf = point1(sheetReady ? sheet!.points : sp.points, 'kopf');
  const height = (kopf ? anchorY - kopf[1] : sp.size?.[1] ?? anchorY) + 1;
  const w = (sp.size?.[0] ?? sheet?.frameSize[0] ?? 20) * k;
  const ringRx = u.radius * pixelsPerUnit() * k;
  // T21: Freunde schauen nach rechts, Sturmwesen nach links; im Sprung schaut die Figur, wohin sie springt (wie in der Skizze des Grafik-Leads)
  const facing = (sheetReady ? sheet!.facing : undefined) ?? sp.facing ?? 'right';
  const dir = u.pose?.dir;
  // K44: sonst schaut sie zu ihrem Ziel (Schlag, Wurf, Laufen); steht das Ziel genau darüber oder darunter, wie gezeichnet
  const look = dir && u.pose?.style !== 'slide' && Math.abs(dir.x) > 0.5 ? Math.sign(dir.x) : (u.pose?.face ?? 0);
  const flip = look !== 0 && (facing === 'right' ? look < 0 : look > 0);
  return {
    x,
    y,
    top: y - height * k,
    cy: y - (height * k) / 2,
    w,
    k,
    ringRx,
    ringRy: Math.max(2 * k, ringRx * 0.36),
    sheet: sheetReady ? sheet : null,
    still: sheetReady ? null : still,
    flip,
  };
}

/** Ring, Schatten und Hervorhebung unter den Füßen; vor der Figur zeichnen */
export function drawFigureGround(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, g: FigureGeo, highlight: boolean): void {
  if (u.kind === 'tree') return;
  const onTree = (u.flags & FrameFlag.onTree) !== 0;
  // Bodenpunkt: im Sprung bleibt der Schatten am Boden (K39)
  const gx = g.x;
  const gy = u.y * s + ((u.pose?.drift ?? 0) + (u.pose?.lunge?.y ?? 0)) * s;
  const lift = (u.pose?.lift ?? 0) * s;
  ctx.save();
  ctx.globalAlpha *= 1 - (u.pose?.fade ?? 0);
  if (lift > 0.5 || u.kind === 'stormling') {
    // Schatten: im Sprung in der Mitte kleiner (K39); Sturmwesen stehen immer auf ihrem Schatten
    const shrink = lift > 0.5 ? Math.max(0.55, 1 - lift / (s * 14)) : 1;
    ctx.fillStyle = 'rgba(10, 12, 20, 0.38)';
    ctx.beginPath();
    ctx.ellipse(gx, gy, g.ringRx * 0.95 * shrink, g.ringRy * shrink, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  if (!onTree && u.kind !== 'stormling') {
    // T21: Ring in der Farbe der Klasse; Befallene auf einem gestrichelten violetten Ring
    ctx.strokeStyle = u.kind === 'infested' ? INFESTED_RING : u.color;
    ctx.lineWidth = Math.max(1.5, g.k);
    if (u.kind === 'infested') ctx.setLineDash([g.k * 2, g.k * 1.5]);
    ctx.beginPath();
    ctx.ellipse(gx, gy, g.ringRx, g.ringRy, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  if (highlight) {
    ctx.strokeStyle = '#f3e9d2';
    ctx.lineWidth = Math.max(1.5, s * 0.35);
    ctx.beginPath();
    ctx.ellipse(gx, gy, g.ringRx + s * 1.4, g.ringRy + s * 0.7, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();
}

/** Die Figur selbst, mit Bewegung, Aufblitzen und den Zeichen am Körper */
export function drawFigure(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, g: FigureGeo, t: number, treeOccupied = false): void {
  ctx.save();
  ctx.globalAlpha *= 1 - (u.pose?.fade ?? 0);
  if (u.kind === 'tree') {
    const back = sprite('marker', 'baum');
    const front = sprite('marker', 'baum-vorn');
    const bi = back ? image(back.file) : null;
    if (back?.anchor && bi) blit(ctx, bi, 0, 0, bi.naturalWidth, bi.naturalHeight, g.x, g.y, back.anchor, g.k);
    const fi = front ? image(front.file) : null;
    if (!treeOccupied && front?.anchor && fi) blit(ctx, fi, 0, 0, fi.naturalWidth, fi.naturalHeight, g.x, g.y, front.anchor, g.k);
    ctx.restore();
    return;
  }
  const pose = u.pose;
  if (g.sheet) {
    const anims = g.sheet.animations;
    const name = pose && anims[pose.anim] ? pose.anim : 'stehen';
    const a = anims[name] ?? Object.values(anims)[0];
    if (a) {
      const at = pose && anims[pose.anim] ? pose.at : t + u.uid * 0.37;
      const f = animFrame(a, at);
      const [fw, fh] = g.sheet.frameSize;
      // T37: Freunde in ihrem Aussehen
      const img = (u.kind === 'friend' ? lookImage(g.sheet.file, u.look, spriteOf(u)) : null) ?? image(g.sheet.file)!;
      if (u.glow) drawGlow(ctx, g.sheet.file, f * fw, a.row * fh, fw, fh, g, u.glow, t);
      const over = (u.fx ?? []).find((x) => sprite(x.kind, x.id)?.layer === ON_FIGURE);
      if (over) drawOnFigure(ctx, img, f * fw, a.row * fh, fw, fh, g, over);
      else blit(ctx, img, f * fw, a.row * fh, fw, fh, g.x, g.y, g.sheet.anchor, g.k, g.flip);
      if (u.glow) drawTint(ctx, g.sheet.file, f * fw, a.row * fh, fw, fh, g, u.glow);
      // T30: kurz weiß aufblitzen, auch mitten im Ausholen
      if (pose?.flash) {
        const white = whiteOf(g.sheet.file);
        if (white) blit(ctx, white, f * fw, a.row * fh, fw, fh, g.x, g.y, g.sheet.anchor, g.k, g.flip);
      }
    }
  } else if (g.still) {
    const img = image(g.still.file)!;
    if (u.glow) drawGlow(ctx, g.still.file, 0, 0, img.naturalWidth, img.naturalHeight, g, u.glow, t);
    const shown = (u.kind === 'friend' ? lookImage(g.still.file, u.look, spriteOf(u)) : null) ?? img;
    blit(ctx, shown, 0, 0, img.naturalWidth, img.naturalHeight, g.x, g.y, g.still.anchor!, g.k, g.flip);
    if (u.glow) drawTint(ctx, g.still.file, 0, 0, img.naturalWidth, img.naturalHeight, g, u.glow);
    if (pose?.flash) {
      const white = whiteOf(g.still.file);
      if (white) blit(ctx, white, 0, 0, img.naturalWidth, img.naturalHeight, g.x, g.y, g.still.anchor!, g.k, g.flip);
    }
  }
  // S5: Laub vor dem Eichhörnchen, das im Baum sitzt
  if ((u.flags & FrameFlag.onTree) !== 0) {
    const front = sprite('marker', 'baum-vorn');
    const fi = front ? image(front.file) : null;
    const tree = sprite('marker', 'baum');
    const sitz = point1(tree?.points, 'sitz');
    if (front?.anchor && fi && tree?.anchor && sitz) {
      blit(ctx, fi, 0, 0, fi.naturalWidth, fi.naturalHeight, g.x, g.y + (tree.anchor[1] - sitz[1]) * g.k, front.anchor, g.k);
    }
  }
  ctx.restore();
}

const ON_FIGURE = 'nur auf der Figur';

/**
 * K38, Feld glow des Grafik-Leads (Version 4): ein Schein um die Figur, die Silhouette width Bildpixel größer, die
 * Deckkraft pulsiert in pulseMs zwischen alpha[0] und alpha[1]
 */
function drawGlow(ctx: CanvasRenderingContext2D, file: string, sx: number, sy: number, sw: number, sh: number, g: FigureGeo, glow: GlowSpec, t: number): void {
  const sil = silhouetteOf(file, glow.color);
  const anchor = g.sheet?.anchor ?? g.still?.anchor;
  if (!sil || !anchor) return;
  const [lo, hi] = glow.alpha;
  const wave = 0.5 + 0.5 * Math.sin((t * 2 * Math.PI * 1000) / Math.max(100, glow.pulseMs));
  const d = Math.max(1, Math.round(glow.width)) * g.k;
  ctx.save();
  ctx.globalAlpha *= lo + (hi - lo) * wave;
  for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) blit(ctx, sil, sx, sy, sw, sh, g.x + dx * d, g.y + dy * d, anchor, g.k, g.flip);
  ctx.restore();
}

/** Die Figur selbst leicht getönt (tint, etwa 0,08 bis 0,16) */
function drawTint(ctx: CanvasRenderingContext2D, file: string, sx: number, sy: number, sw: number, sh: number, g: FigureGeo, glow: GlowSpec): void {
  const sil = silhouetteOf(file, glow.color);
  const anchor = g.sheet?.anchor ?? g.still?.anchor;
  if (!sil || !anchor) return;
  ctx.save();
  ctx.globalAlpha *= glow.tint;
  blit(ctx, sil, sx, sy, sw, sh, g.x, g.y, anchor, g.k, g.flip);
  ctx.restore();
}

let scratch: HTMLCanvasElement | null = null;

/** K38 Zäh: Der harte Glanz läuft nur über die Pixel der Figur (layer „nur auf der Figur“, source-atop auf einer Kopie) */
function drawOnFigure(ctx: CanvasRenderingContext2D, img: CanvasImageSource, sx: number, sy: number, fw: number, fh: number, g: FigureGeo, f: ActiveFx): void {
  const sheet = g.sheet!;
  const sp = sprite(f.kind, f.id)!;
  const fi = image(sp.file);
  const frame = fxFrame(sp, f.age);
  if (!scratch) scratch = document.createElement('canvas');
  if (scratch.width !== fw || scratch.height !== fh) {
    scratch.width = fw;
    scratch.height = fh;
  }
  const c = scratch.getContext('2d')!;
  c.clearRect(0, 0, fw, fh);
  c.drawImage(img, sx, sy, fw, fh, 0, 0, fw, fh);
  if (fi && frame !== null) {
    const [ew, eh] = sp.frameSize ?? [fi.naturalWidth, fi.naturalHeight];
    const center = sp.center ?? [Math.floor(ew / 2), Math.floor(eh / 2)];
    const m = point1(sheet.points, 'wunde') ?? [sheet.anchor[0], Math.round(sheet.anchor[1] / 2)];
    c.globalCompositeOperation = 'source-atop';
    c.drawImage(fi, frame * ew, 0, ew, eh, m[0] - center[0], m[1] - center[1], ew, eh);
    c.globalCompositeOperation = 'source-over';
  }
  blit(ctx, scratch, 0, 0, fw, fh, g.x, g.y, sheet.anchor, g.k, g.flip);
}

/**
 * Version 3 des Manifests: Bilder für Kniffe und Eigenschaften an der Figur, je Ebene (boden unter allen Figuren,
 * hinter vor der Figur, vor nach ihr). Der Punkt kommt aus dem Manifest (kopf, kopfmitte, mitte, fuss, augen, mund, ruf;
 * ziel:… sitzt am Getroffenen, der das Bild trägt); gezeichnet für eine Blickrichtung, sonst gespiegelt.
 */
export function drawFx(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, g: FigureGeo, layer: 'boden' | 'hinter' | 'vor'): void {
  if (!u.fx?.length) return;
  ctx.save();
  ctx.globalAlpha *= 1 - (u.pose?.fade ?? 0);
  for (const f of u.fx) {
    const sp = sprite(f.kind, f.id);
    if (!sp) continue;
    // Runde 7: Kränze mit splitRow (Stöckchenwall) teilen sich wie bedroht-<rx>: hinten mit dem Ring unter der Figur, vorn darüber
    const split = sp.splitRow !== undefined && sp.layerBack && sp.frameSize ? sp.splitRow : undefined;
    if (split === undefined ? (sp.layer ?? 'vor') !== layer : layer === 'boden') continue;
    const frame = fxFrame(sp, f.age);
    if (frame === null) continue;
    const right = f.self && g.flip ? !f.faceRight : f.faceRight;
    const flip = (sp.facing ?? 'right') === 'right' ? !right : right;
    const name = (f.point ?? sp.point ?? 'mitte').replace(/^ziel:/, '');
    let pts = f.at ? [{ x: f.at.x * s, y: f.at.y * s }] : pointsOf(g, name);
    if (!pts.length) pts = [{ x: g.x, y: name === 'fuss' ? g.y : name === 'kopf' || name === 'kopfmitte' ? g.top + 2 * g.k : g.cy }];
    const [ox, oy] = sp.offset ?? [0, 0];
    const dx = ox * g.k * (right ? 1 : -1) + (f.back ? (right ? -1 : 1) * g.ringRx : 0);
    if (split !== undefined) {
      const img = image(sp.file);
      if (!img) continue;
      const [fw, fh] = sp.frameSize!;
      const c = sp.center ?? [Math.floor(fw / 2), Math.floor(fh / 2)];
      const [y0, h] = layer === 'hinter' ? [0, split] : [split, fh - split];
      for (const p of pts) if (h > 0) blit(ctx, img, frame * fw, y0, fw, h, p.x + dx, p.y + oy * g.k, [c[0], c[1] - 0.5 - y0], g.k, flip);
      continue;
    }
    for (const p of pts) drawStrip(ctx, f.kind, f.id, frame, p.x + dx, p.y + oy * g.k, g.k, { flip, ...(sp.alpha !== undefined ? { alpha: sp.alpha } : {}) });
  }
  ctx.restore();
}

/** Fabi (Phase Kern): Benommen sieht man sofort, die Figur wird grau */
export function drawFigureVeil(ctx: CanvasRenderingContext2D, u: DrawUnit, g: FigureGeo, t: number): void {
  const sheet = g.sheet;
  const file = sheet?.file ?? g.still?.file;
  if (!file) return;
  const veil = silhouetteOf(file, '#8c8c96');
  if (!veil) return;
  ctx.save();
  ctx.globalAlpha *= 0.5;
  if (sheet) {
    const anims = sheet.animations;
    const name = u.pose && anims[u.pose.anim] ? u.pose.anim : 'stehen';
    const a = anims[name] ?? Object.values(anims)[0];
    if (a) {
      const f = animFrame(a, u.pose && anims[u.pose.anim] ? u.pose.at : t + u.uid * 0.37);
      const [fw, fh] = sheet.frameSize;
      blit(ctx, veil, f * fw, a.row * fh, fw, fh, g.x, g.y, sheet.anchor, g.k, g.flip);
    }
  } else if (g.still) {
    blit(ctx, veil, 0, 0, veil.width, veil.height, g.x, g.y, g.still.anchor!, g.k, g.flip);
  }
  ctx.restore();
}

/**
 * Die Punkte der Figur (kopf, wunde, wurf, augen …) in Gerätepixeln. Version 3 des Manifests: kopfmitte ist der Kopf
 * und 5 Bildpixel tiefer, mitte der Punkt wunde, fuss der Fußpunkt; augen sind zwei Punkte
 */
export function pointsOf(g: FigureGeo, name: string): { x: number; y: number }[] {
  const sheet = g.sheet;
  const pts = sheet?.points ?? g.still?.points;
  const anchor = sheet?.anchor ?? g.still?.anchor;
  if (!anchor) return [];
  const list: [number, number][] =
    name === 'fuss' ? [anchor] : name === 'kopfmitte' ? pointList(pts, 'kopf').map(([x, y]) => [x, y + 5]) : name === 'mitte' ? pointList(pts, 'wunde') : pointList(pts, name);
  return list.map((p) => ({ x: g.x + (p[0] - anchor[0]) * g.k * (g.flip ? -1 : 1), y: g.y + (p[1] - anchor[1]) * g.k }));
}

/** Ein Punkt der Figur in Gerätepixeln, oder null */
export const pointOf = (g: FigureGeo, name: string): { x: number; y: number } | null => pointsOf(g, name)[0] ?? null;

/** Zustände als Bilder an der Figur: Wunde, benommen, Dreck in den Augen, langsam (F42, T30) */
export function drawFigureStatus(ctx: CanvasRenderingContext2D, s: number, u: DrawUnit, g: FigureGeo, t: number): boolean {
  if (u.kind === 'tree') return false;
  let woundDrawn = false;
  ctx.save();
  ctx.globalAlpha *= 1 - (u.pose?.fade ?? 0);
  if (u.wounded) {
    const p = pointOf(g, 'wunde') ?? { x: g.x - g.w * 0.25, y: g.cy };
    woundDrawn = drawStrip(ctx, 'status', 'wunde', 0, p.x, p.y, g.k);
  }
  const head = pointOf(g, 'kopf') ?? { x: g.x, y: g.top };
  // Version 4: Mit den kleinen Zeichen neben der Leiste fallen die Sterne über dem Kopf weg, der graue Schleier bleibt
  if ((u.flags & FrameFlag.stunned) !== 0 && !sprite('status', 'benommen-klein')) drawStrip(ctx, 'status', 'benommen', stripFrame('status', 'benommen', t), head.x, head.y - 3 * g.k, g.k);
  if ((u.flags & FrameFlag.blinded) !== 0) drawStrip(ctx, 'status', 'geblendet', 0, head.x, head.y + 4 * g.k, g.k);
  if ((u.flags & FrameFlag.slowed) !== 0) drawStrip(ctx, 'status', 'verlangsamt', 0, g.x, u.y * s - g.k, g.k);
  // S10 Leibwache: ein kleiner Schild über dem Schützling
  if (u.warded && !sprite('status', 'leibwache-schuetzling')) drawStrip(ctx, 'status', 'leibwache', 0, g.x + g.w * 0.3, g.top - 2 * g.k, g.k);
  // K38: Die Eigenschaft sieht man auch im Kampf: Tückisch funkelt am Kopf, Hastig zieht Windstriche hinter sich her
  const ids = u.traitIds ?? [];
  // Version 3: mit den Anzeigen des Grafik-Leads (drawFx); die Zeichen aus Code bleiben, falls sie fehlen
  if (ids.includes('tueckisch') && !sprite('status', 'anzeige-tueckisch')) drawSparkle(ctx, head.x - 3 * g.k, head.y + 2 * g.k, g.k, t + u.uid * 0.9);
  if (ids.includes('hastig') && !sprite('status', 'anzeige-hastig')) drawHaste(ctx, g, u, t);
  ctx.restore();
  return woundDrawn;
}

/** K38 Tückisch: ein Funkeln am Kopf, alle paar Sekunden, wie ein listiges Zwinkern */
function drawSparkle(ctx: CanvasRenderingContext2D, x: number, y: number, k: number, t: number): void {
  const cycle = 2.6;
  const phase = ((t % cycle) + cycle) % cycle;
  if (phase > 0.42) return;
  const a = Math.sin((phase / 0.42) * Math.PI);
  const r = Math.round((2 + 2 * a) * 1) * k;
  ctx.save();
  ctx.globalAlpha *= 0.35 + 0.65 * a;
  ctx.fillStyle = TRAIT_COLOR;
  const px = Math.round(x);
  const py = Math.round(y);
  ctx.fillRect(px - k / 2, py - r, k, r * 2);
  ctx.fillRect(px - r, py - k / 2, r * 2, k);
  ctx.fillStyle = '#f4ecff';
  ctx.fillRect(px - k / 2, py - k / 2, k, k);
  ctx.restore();
}

/** K38 Hastig: Windstriche hinter dem Sturmwesen, leicht nach vorn geneigt; im Stand schwach, beim Laufen deutlich */
function drawHaste(ctx: CanvasRenderingContext2D, g: FigureGeo, u: DrawUnit, t: number): void {
  const moving = u.pose?.anim === 'laufen' || !!u.pose?.dir;
  // Sturmwesen schauen nach links; „hinter“ ihnen ist rechts
  const back = u.side === 'enemy' ? 1 : -1;
  ctx.save();
  ctx.globalAlpha *= moving ? 0.85 : 0.3;
  ctx.fillStyle = TRAIT_COLOR;
  for (let i = 0; i < 3; i++) {
    const wobble = Math.round(Math.sin(t * 9 + i * 2.1) * 1.2) * g.k;
    const len = (5 + ((i * 3) % 4)) * g.k;
    const y = Math.round(g.cy + (i - 1) * 4 * g.k);
    const x = Math.round(g.x + back * (g.w / 2 + 2 * g.k) + wobble * back);
    ctx.fillRect(back > 0 ? x : x - len, y, len, g.k);
  }
  ctx.restore();
}

/** K39: Bewegungsstriche, immer hinter der Bewegung; Staub beim Huschen */
export function drawMotionLines(ctx: CanvasRenderingContext2D, u: DrawUnit, g: FigureGeo, t: number): void {
  const dir = u.pose?.dir;
  if (!dir || u.pose?.style === 'burrow') return;
  const len = Math.hypot(dir.x, dir.y);
  if (len < 1e-6) return;
  const nx = dir.x / len;
  const ny = dir.y / len;
  ctx.save();
  ctx.globalAlpha *= 0.75;
  ctx.strokeStyle = '#e9e4d6';
  ctx.lineWidth = g.k;
  ctx.lineCap = 'butt';
  for (let i = -1; i <= 1; i++) {
    // quer zur Bewegung versetzt, hinter der Figur
    const ox = -ny * i * 4 * g.k;
    const oy = nx * i * 4 * g.k;
    const start = g.w / 2 + (2 + Math.abs(i) * 2) * g.k;
    const l = (7 - Math.abs(i) * 2) * g.k;
    const sx = g.x - nx * start + ox;
    const sy = g.cy - ny * start + oy;
    ctx.beginPath();
    ctx.moveTo(Math.round(sx), Math.round(sy));
    ctx.lineTo(Math.round(sx - nx * l), Math.round(sy - ny * l));
    ctx.stroke();
  }
  ctx.restore();
  // Der Feuersalamander huscht mit kleinen Staubwölkchen hinter sich (K39)
  if (u.pose?.style === 'dash' || u.pose?.style === 'slide') {
    drawStrip(ctx, 'effect', 'staub', stripFrame('effect', 'staub', t), g.x - nx * g.w * 0.6, g.y + (u.pose?.lift ?? 0), g.k, { alpha: 0.8 });
  }
}

/** K38: Zeichen der Eigenschaften neben der Lebensleiste; die Flächen merkt sich das Ufer für die Erklärung beim Darüberfahren */
export type TraitSpot = { x: number; y: number; w: number; h: number; text: string };
export const traitSpots: TraitSpot[] = [];

export function drawSigns(ctx: CanvasRenderingContext2D, u: DrawUnit, g: FigureGeo, barX: number, barY: number, barW: number, barH: number, classIcon = true): number {
  const k = g.k;
  const size = 9 * k;
  const gap = 2 * k;
  const mid = barY + barH / 2;
  let left = barX - barW / 2 - gap;
  // T21: das Zeichen der Klasse links vor dem Lebensbalken
  if (classIcon && (u.kind === 'friend' || u.kind === 'infested') && u.friendClass) {
    if (drawIcon(ctx, 'class', u.friendClass, left - size / 2, mid, k)) left -= size + gap;
  }
  const ids = u.traitIds ?? [];
  let right = barX + barW / 2 + gap;
  for (const id of ids) {
    const sp = sprite('trait', id);
    if (!sp) continue;
    // Sturmwesen tragen ihre Eigenschaften vorn, Befallene hinter dem Balken (vorn steht ihre Klasse)
    const atLeft = u.kind === 'stormling';
    const cx = atLeft ? left - size / 2 : right + size / 2;
    if (!drawIcon(ctx, 'trait', id, cx, mid, k)) continue;
    const tr = getTrait(id);
    traitSpots.push({ x: cx - size / 2, y: mid - size / 2, w: size, h: size, text: `${tr.name}: ${tr.text}` });
    if (atLeft) left -= size + gap;
    else right += size + gap;
  }
  return right;
}

function drawIcon(ctx: CanvasRenderingContext2D, kind: 'class' | 'trait', id: string, cx: number, cy: number, k: number): boolean {
  const sp = sprite(kind, id);
  const img = sp ? image(sp.file) : null;
  if (!sp || !img) return false;
  blit(ctx, img, 0, 0, img.naturalWidth, img.naturalHeight, cx, cy, [Math.floor(img.naturalWidth / 2), Math.floor(img.naturalHeight / 2) - 0.5], k);
  return true;
}

/** F32: Giftstapel neben der Lebensleiste, mit Zahl (zustand-gift) */
export function drawPoisonSign(ctx: CanvasRenderingContext2D, s: number, x: number, y: number, k: number, n: number): boolean {
  if (!drawStrip(ctx, 'status', 'gift', 0, x, y, k)) return false;
  ctx.save();
  ctx.fillStyle = '#b7e39f';
  ctx.strokeStyle = 'rgba(10, 20, 8, 0.85)';
  ctx.lineWidth = Math.max(1, s * 0.3);
  ctx.font = `700 ${Math.max(9, s * 1.7)}px system-ui, sans-serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.strokeText(String(n), x + 3.5 * k, y);
  ctx.fillText(String(n), x + 3.5 * k, y);
  ctx.restore();
  return true;
}

// ---------- Geschosse und Effekte im Kampf (T31, K39, T30) ----------

/** Ereignisse zwischen t0 und t1, über die sortierte Liste gesucht */
function eventsBetween(r: BattleResult, t0: number, t1: number): BattleEvent[] {
  const ev = r.events;
  let lo = 0;
  let hi = ev.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (ev[mid]!.t < t0) lo = mid + 1;
    else hi = mid;
  }
  const out: BattleEvent[] = [];
  for (let i = lo; i < ev.length && ev[i]!.t <= t1 + 1e-9; i++) out.push(ev[i]!);
  return out;
}

/**
 * Was zwischen den Figuren fliegt und aufblitzt: Geschosse vom Punkt wurf bis zum Ziel, Funken bei Treffern, Heilung,
 * Gift, Splitter, Staub bei der Landung und das Verpuffen vertriebener Sturmwesen. Nur mit Figuren; die Kreise zeigen
 * es wie bisher mit Linien und Zahlen.
 */
export function drawArtEffects(ctx: CanvasRenderingContext2D, s: number, units: DrawUnit[], r: BattleResult, T: number): void {
  if (!figuresWanted()) return;
  const k = pixelScale(s);
  const byUid = new Map(units.map((u) => [u.uid, u]));
  const geo = new Map<number, FigureGeo | null>();
  const geoOf = (uid: number) => {
    if (!geo.has(uid)) {
      const u = byUid.get(uid);
      geo.set(uid, u ? figureGeo(u, s) : null);
    }
    return geo.get(uid) ?? null;
  };
  /** Körpermitte in Gerätepixeln, auch ohne Figur */
  const centerOf = (uid: number) => {
    const g = geoOf(uid);
    if (g) return { x: g.x, y: g.cy };
    const u = byUid.get(uid);
    return u ? { x: u.x * s, y: u.y * s } : null;
  };
  const m = motionOf(r);
  ctx.save();
  // Geschosse im Flug
  for (const sh of m.shots) {
    if (T < sh.release || T > sh.t + 0.4) continue;
    const tg = sh.toPoint ? geoOf(sh.to) : null;
    let to = (tg && sh.toPoint && pointOf(tg, sh.toPoint)) || centerOf(sh.to);
    // K47: Ein echtes Geschoss fliegt zu seinem Zielort, in Körperhöhe des Ziels; ging es ins Leere oder an einen Stein, dort
    if (sh.aim) {
      const hg = geoOf(sh.to);
      const body = hg ? hg.y - hg.cy : 2.5 * s;
      to = { x: sh.aim.x * s, y: sh.aim.y * s - (sh.hit ? body : Math.min(body, 2.5 * s)) };
    }
    if (!to) continue;
    if (T >= sh.t) {
      // Ankunft: Funke, Klatscher (Schlamm, Dreck); Heilung zeigt das Heilen selbst; eigene Bilder bringt der Kniff mit
      if (!sh.heal && sh.impact !== null) {
        const shooter = byUid.get(sh.from);
        const id = sh.mud ? 'klatscher' : shooter?.side === 'enemy' ? 'funke-sturm' : 'funke-freund';
        if (T - sh.t < stripSeconds('effect', id)) drawStrip(ctx, 'effect', id, stripFrame('effect', id, T - sh.t), to.x, to.y, k);
      }
      continue;
    }
    const g = geoOf(sh.from);
    const shooter = byUid.get(sh.from);
    const dirX = to.x - (shooter ? shooter.x * s : to.x);
    const from = (g && pointOf(g, sh.fromPoint ?? 'wurf')) ?? (g ? { x: g.x + Math.sign(dirX || 1) * g.w * 0.3, y: g.cy } : shooter ? { x: shooter.x * s, y: shooter.y * s } : null);
    if (!from) continue;
    const q = (T - sh.release) / Math.max(1e-6, sh.t - sh.release);
    const d = Math.hypot(to.x - from.x, to.y - from.y);
    const lift = sh.arc * d * 4 * q * (1 - q);
    const x = from.x + (to.x - from.x) * q;
    const y = from.y + (to.y - from.y) * q - lift;
    if (sh.arc > 0.05) {
      // Schatten am Boden unter dem Geschoss (K39)
      const endY = sh.aim ? sh.aim.y : (byUid.get(sh.to)?.y ?? 0);
      const gy = (byUid.get(sh.from)?.y ?? 0) * s + (endY * s - (byUid.get(sh.from)?.y ?? 0) * s) * q;
      drawStrip(ctx, 'effect', 'schatten-klein', 0, x, gy, k, { alpha: 0.6 });
    }
    // Richtung im Moment, für Spiegeln und die Neigung des Federpfeils
    const vy = (to.y - from.y) - sh.arc * d * 4 * (1 - 2 * q);
    const vx = to.x - from.x;
    const sp = sprite('projectile', sh.projectile);
    let frame = stripFrame('projectile', sh.projectile, T - sh.release);
    if (sp?.angles) frame = Math.abs(vy) < Math.abs(vx) * 0.25 ? 0 : vy < 0 ? 1 : 2;
    // T37, Version 4: Der Federpfeil trägt die Farben des Eisvogels, der ihn wirft
    const look = sp?.palettesFrom && shooter?.kind === 'friend' ? shooter.look : 0;
    const img = look && sp ? lookImage(sp.file, look, sprite('species', sp.palettesFrom!)) : null;
    drawStrip(ctx, 'projectile', sh.projectile, frame, x, y, k, { flip: vx < 0, img });
  }
  // Funken bei Treffern aus der Nähe, Splitter, Gift, Heilung, Verpuffen. Version 4: Der Hiebbogen beginnt kurz vor
  // dem Treffer (leadMs), darum schauen wir ein Stück voraus
  for (const e of eventsBetween(r, T - 0.45, T + 0.1)) {
    const age = T - e.t;
    if (age < -1e-9 && !(e.type === 'damage' && (e.source === 'hit' || e.source === 'kniff'))) continue;
    if (e.type === 'damage') {
      const at = centerOf(e.to);
      if (!at) continue;
      if (e.source === 'splash' && age < stripSeconds('effect', 'splitter')) drawStrip(ctx, 'effect', 'splitter', stripFrame('effect', 'splitter', age), at.x, at.y, k);
      else if (e.source === 'poison' && age < stripSeconds('effect', 'gift')) drawStrip(ctx, 'effect', 'gift', stripFrame('effect', 'gift', age), at.x, at.y - 2 * k, k);
      else if ((e.source === 'hit' || e.source === 'kniff') && e.from !== null && !m.shots.some((sh) => sh.from === e.from && sh.to === e.to && Math.abs(sh.t - e.t) < 1e-6)) {
        // Version 3: Kniffe mit eigenem Trefferbild und Zähe (graue Splitter) brauchen keinen Funken
        const kniff = e.source === 'kniff' ? r.units[e.from]?.kniff : null;
        if (kniff && KNIFF_HITS[kniff] && sprite('effect', KNIFF_HITS[kniff]!)) continue;
        if (r.units[e.to]?.traits.includes('zaeh') && sprite('status', 'anzeige-zaeh-treffer')) continue;
        const from = byUid.get(e.from);
        // Version 4 (Vorschlag des Grafik-Leads): Bei Nahkampftreffern zeigt ein Hiebbogen in der Neigung des Schlags,
        // wohin er geht (flach, hoch, runter; nach links gespiegelt); er ersetzt den Funken
        if (from && drawSlash(ctx, from, at, e.t, T, k, s)) continue;
        if (age < 0) continue;
        const id = from?.side === 'enemy' ? 'funke-sturm' : 'funke-freund';
        if (age < stripSeconds('effect', id)) {
          // der Funke sitzt an der Seite, von der der Schlag kommt
          const side = from ? Math.sign(from.x * s - at.x) : 0;
          const g = geoOf(e.to);
          drawStrip(ctx, 'effect', id, stripFrame('effect', id, age), at.x + side * (g ? g.w * 0.3 : 0), at.y, k);
        }
      }
    } else if (e.type === 'heal' && e.amount >= 1 && !(e.via === 'kniff:wunden-auswaschen' && sprite('effect', 'auswaschen'))) {
      const at = centerOf(e.to);
      if (at && age < stripSeconds('effect', 'heilen')) drawStrip(ctx, 'effect', 'heilen', stripFrame('effect', 'heilen', age), at.x, at.y - 3 * k, k);
    } else if (e.type === 'down') {
      const u = byUid.get(e.unit);
      const at = centerOf(e.unit);
      if (u?.kind === 'stormling' && at && age < stripSeconds('effect', 'verpuffen')) drawStrip(ctx, 'effect', 'verpuffen', stripFrame('effect', 'verpuffen', age), at.x, at.y, k);
    }
  }
  // Staub, wo jemand nach einem Sprung landet (K39)
  for (const d of m.dust) {
    const age = T - d.t;
    if (age < 0 || age > stripSeconds('effect', 'staub')) continue;
    drawStrip(ctx, 'effect', 'staub', stripFrame('effect', 'staub', age), d.x * s, d.y * s, k, { alpha: 0.85 });
  }
  ctx.restore();
}

/** Wo man eine Figur trifft (ihr Körper), in Feldeinheiten; null ohne Figur */
/**
 * Version 4: der Hiebbogen eines Nahkampftreffers. Die Neigung folgt dem Winkel vom Angreifer zum Ziel (bis etwa 20 Grad
 * flach); gezeichnet für einen Hieb nach rechts, nach links gespiegelt. false, wenn es das Bild nicht gibt
 */
function drawSlash(ctx: CanvasRenderingContext2D, from: DrawUnit, at: { x: number; y: number }, hitT: number, T: number, k: number, s: number): boolean {
  const dx = at.x - from.x * s;
  const dy = at.y - from.y * s;
  const deg = (Math.atan2(-dy, Math.abs(dx)) * 180) / Math.PI;
  const tilt = Math.abs(deg) <= 20 ? 'flach' : deg > 0 ? 'hoch' : 'runter';
  const id = `hiebbogen-${tilt}${from.side === 'enemy' ? '-sturm' : ''}`;
  const sp = sprite('effect', id);
  if (!sp) return false;
  const age = T - (hitT - (sp.leadMs ?? 0) / 1000);
  if (age < 0) return true;
  const frame = fxFrame(sp, age);
  if (frame === null) return true;
  drawStrip(ctx, 'effect', id, frame, at.x, at.y, k, { flip: dx < 0 });
  return true;
}

export function figureBox(u: DrawUnit, s: number): { x0: number; x1: number; y0: number; y1: number; cx: number; cy: number } | null {
  const g = figureGeo(u, s);
  if (!g || u.kind === 'tree') return null;
  const half = Math.max(g.w / 2, g.ringRx);
  return { x0: (g.x - half) / s, x1: (g.x + half) / s, y0: g.top / s, y1: (g.y + g.ringRy) / s, cx: g.x / s, cy: g.cy / s };
}
